@if (auditResult) {
<div class="ai-audit-container">
  <div class="ai-audit-scroll-container">

    <!-- Unified View: Single composite score + single-page audit + multi-page sections -->
    <div class="unified-ai-audit-view">

      <!-- Row 1: AI Audit Score side-by-side with Radar Chart -->
      <div class="row-1-score-chart">
        <div class="composite-score-container">
          <app-composite-score-widget [compositeScore]="compositeScore" [title]="'AI Audit Score'" [size]="'medium'">
          </app-composite-score-widget>
        </div>

        <!-- Radar Chart side-by-side -->
        @if (areaChartData && areaChartData.length > 0) {
        <div class="chart-section-inline">
          <div class="chart-header">
            <div class="chart-title-section">
              <h4 class="chart-title">Current and Potential Scores</h4>
              <p class="chart-subtitle">Comparison of current scores vs potential improvement across audit categories
              </p>
            </div>
          </div>
          <div class="area-chart-wrapper-inline">
            <app-area-chart [areaChartData]="areaChartData" [scheme]="'vivid'" [animations]="true" [legend]="true"
              [legendTitle]="'Scores'" [xAxis]="true" [yAxis]="true" [showXAxisLabel]="false" [showYAxisLabel]="false"
              [xAxisLabel]="''" [yAxisLabel]="''" [maxValue]="100">
            </app-area-chart>
          </div>
        </div>
        }
      </div>

      <!-- Row 2: Dynamic Categories Cards from CSV -->
      @if (getCategories().length > 0) {
      <div class="summary-cards-grid">
        @for (category of getCategories(); track category) {
        @let currentScore = getCategoryCurrentScore(category);
        @let passedInfo = getCategoryPassedQuestions(category);
        <div class="summary-card">
          <div class="card-header">
            <mat-icon>{{ getCategoryIcon(category) }}</mat-icon>
            <h4>{{ category }}</h4>
          </div>
          <div class="card-content">
            <div class="score-display">
              <span class="score-value" [style.color]="getScoreColor(currentScore)">
                {{ round(currentScore) }}%
              </span>
              <div class="progress-bar">
                <div class="progress-fill" [style.width.%]="currentScore"
                  [style.background]="getProgressColor(currentScore)">
                </div>
              </div>
            </div>
            <div class="card-description">
              {{ passedInfo.passed }}/{{ passedInfo.total }} questions passed
            </div>
          </div>
        </div>
        }
      </div>
      }

      <!-- Row 4: Audit Categories Summary Table -->
      @if (getCategories().length > 0) {
      <div class="results-section zero-padding m-b-10">
        <div class="performance-table">
          <div class="table-header">
            <div class="table-title-section">
              <h3>Audit Categories Summary</h3>
            </div>
          </div>
          <div class="table-container">
            <table class="audit-table">
              <thead>
                <tr>
                  <th>Category</th>
                  <th>Current Score</th>
                  <th>Potential Score</th>
                  <!-- <th>Status</th>
                  <th>Questions</th> -->
                </tr>
              </thead>
              <tbody>
                @for (category of getCategories(); track category) {
                @let areaData = auditAreas[category];
                @let currentScore = getCategoryCurrentScore(category);
                <!-- @let passedInfo = getCategoryPassedQuestions(category); -->
                <tr>
                  <td class="category-cell">{{ category }}</td>
                  <td>
                    <span class="score-value" [style.color]="getScoreColor(currentScore)">
                      {{ currentScore }}/100
                    </span>
                  </td>
                  <td>
                    <span class="score-value" [style.color]="getScoreColor(areaData.potential)">
                      {{ areaData.potential }}/100
                    </span>
                  </td>
                  <!-- <td>
                    @if (isPass(currentScore)) {
                    <span class="status-badge pass">
                      <mat-icon>check_circle</mat-icon>
                      Pass
                    </span>
                    } @else {
                    <span class="status-badge fail">
                      <mat-icon>cancel</mat-icon>
                      Fail
                    </span>
                    }
                  </td>
                  <td class="questions-cell">
                    {{ passedInfo.passed }}/{{ passedInfo.total }} passed
                  </td> -->
                </tr>
                }
              </tbody>
            </table>
          </div>
        </div>
      </div>
      }

      <!-- Row 3: Discovered Pages Table (Matching Image Design) -->
      @if (hasDiscoveryPagesNA()) {
      <div class="discovered-pages-section">
        <h3>Discovered Pages</h3>
        <div class="discovered-pages-na">
          <p>NA - No site URL found in project description</p>
        </div>
      </div>
      } @else if (loading && getMultiPageDiscoveredPages().length === 0) {
      <!-- Show loader when pages are being discovered -->
      <div class="discovered-pages-section">
        <h3>Discovered Pages</h3>
        <div class="discovered-pages-loading">
          <app-common-circle-loader message="Discovering pages from your website... This may take a few moments."></app-common-circle-loader>
        </div>
      </div>
      } @else if (getMultiPageDiscoveredPages().length === 0 && !loading && auditResult) {
      <!-- Show message when discovery completed but no pages found -->
      <div class="discovered-pages-section">
        <h3>Discovered Pages</h3>
        <div class="discovered-pages-na">
          <div class="pending-message-container">
            <mat-icon class="pending-icon-large">search_off</mat-icon>
            <div class="pending-message-content">
              <h4>No Pages Discovered</h4>
              <p>We couldn't discover any pages from your website. Please check if the URL is correct and accessible.</p>
            </div>
          </div>
        </div>
      </div>
      } @else if (getMultiPageDiscoveredPages().length > 0) {
      <div class="discovered-pages-section">
        <h3>Discovered Pages</h3>
        <div class="discovered-pages-table-container">
          <table class="discovered-pages-table">
            <thead>
              <tr>
                <th class="page-col">Page</th>
                @for (category of getCategories(); track category) {
                <th class="metric-col sortable">
                  {{ category }}
                </th>
                }
              </tr>
            </thead>
            <tbody>
              @for (pageData of getDiscoveredPagesWithMetrics(); track pageData.url) {
              <tr [class.expanded]="isPageExpanded(pageData.url)">
                <td class="page-cell">
                  <button class="expand-icon-button" (click)="togglePageDetails(pageData.url)"
                    [class.expanded]="isPageExpanded(pageData.url)">
                    <mat-icon>{{ isPageExpanded(pageData.url) ? 'remove' : 'add' }}</mat-icon>
                  </button>
                  <a [href]="pageData.url" target="_blank" rel="noopener noreferrer" class="page-url-link">
                    {{ pageData.url }}
                  </a>
                </td>
                @for (category of getCategories(); track category) {
                <td class="metric-cell">
                  <span [style.color]="getScoreColor(getPageCategoryScore(pageData, category))">
                    {{ getPageCategoryScore(pageData, category) }}%
                  </span>
                </td>
                }
              </tr>
              <!-- Expanded Row Content -->
              @if (isPageExpanded(pageData.url) && pageData.metrics) {
              <tr class="expanded-content-row">
                <td [attr.colspan]="getCategories().length + 1" class="expanded-content-cell">
                  <div class="page-metrics-expanded-grid">
                    @for (category of getCategories(); track category) {
                    @let categoryData = getPageCategoryData(pageData, category);
                    <div class="expanded-metric-card">
                      <div class="expanded-card-header">
                        <h5>{{ category }}</h5>
                        <div class="expanded-score"
                          [style.color]="getScoreColor(getPageCategoryScore(pageData, category))">
                          {{ getPageCategoryScore(pageData, category) }}%
                        </div>
                      </div>
                      <div class="expanded-progress-bar">
                        <div class="expanded-progress-fill" [style.width.%]="getPageCategoryScore(pageData, category)"
                          [style.background]="getProgressColor(getPageCategoryScore(pageData, category))">
                        </div>
                      </div>
                      <p class="expanded-description">{{ getPageCategoryDescription(pageData, category) || 'N/A' }}</p>

                      <!-- Sub-metrics (if available) -->
                      @if (categoryData && categoryData.sub_metrics && categoryData.sub_metrics.length > 0) {
                      <div class="expanded-sub-metrics">
                        @for (subMetric of categoryData.sub_metrics; track subMetric.name) {
                        <div class="sub-metric-item">
                          <div class="sub-metric-label">{{ subMetric.name }}:</div>
                          <div class="sub-metric-value">{{ subMetric.value }}</div>
                          @if (subMetric.percentage !== undefined) {
                          <div class="sub-metric-percentage">{{ subMetric.percentage }}%</div>
                          <div class="sub-progress-bar">
                            <div class="sub-progress-fill" [style.width.%]="subMetric.percentage"
                              [style.background]="getProgressColor(subMetric.percentage)">
                            </div>
                          </div>
                          }
                        </div>
                        }
                      </div>
                      }

                      <!-- Detailed Breakdown (if available) -->
                      @if (categoryData && categoryData.breakdown && categoryData.breakdown.length > 0) {
                      <div class="expanded-structured-details">
                        @for (breakdownSection of categoryData.breakdown; track breakdownSection.section) {
                        <div class="structured-section">
                          <strong>{{ breakdownSection.section }}:</strong>
                          <div class="structured-items">
                            @for (item of breakdownSection.items; track item.label) {
                            <div class="structured-item" [class.yes]="item.value === 'Yes' || item.value === true"
                              [class.no]="item.value === 'No' || item.value === false">
                              <span class="structured-label">{{ item.label }}</span>
                              <span class="structured-value">
                                @if (item.value === 'Yes' || item.value === true) {
                                Yes
                                <mat-icon>check_circle</mat-icon>
                                } @else if (item.value === 'No' || item.value === false) {
                                No
                                <mat-icon>cancel</mat-icon>
                                } @else {
                                {{ item.value }}
                                }
                              </span>
                            </div>
                            }
                          </div>
                        </div>
                        }
                      </div>
                      }

                      <!-- Optimization Note (if available) -->
                      @if (categoryData && categoryData.optimization_note) {
                      <div class="expanded-optimization">
                        <strong>Optimization Note:</strong>
                        <p>{{ categoryData.optimization_note }}</p>
                      </div>
                      }
                    </div>
                    }
                  </div>
                </td>
              </tr>
              }
              }
            </tbody>
          </table>
        </div>
      </div>
      }
    </div>
  </div>

  <!-- HIDDEN: Evidence Sources / Data Sources & Citations (do not remove)
  @if (getEvidenceSources().length > 0) {
  <app-citation-card [citations]="getEvidenceSources()" [title]="'Evidence Sources (Tavily)'" [displayStyle]="'grid'"
    [maxDisplay]="6">
  </app-citation-card>
  }
  -->

  

      <!-- Row 5: Accordion (Questions by Category) -->
      @if (getCategories().length > 0) {
      @for (category of getCategories(); track category) {
      @let questions = getQuestionsByCategory(category);
      @if (questions.length > 0) {
      <div class="question-category-section">
        <!-- Expandable Header -->
        <div class="question-category-header" (click)="toggleCategoryExpansion(category)">
          <div class="category-header-content">
            <button mat-icon-button class="expand-button" [attr.aria-label]="'Toggle ' + category + ' questions'">
              <mat-icon>{{ isCategoryExpanded(category) ? 'remove' : 'add' }}</mat-icon>
            </button>
            <h3 class="category-title">{{ category }} - Questions</h3>
            <span class="question-count">{{ getTotalCount(questions) }} questions are scored</span>
          </div>
        </div>

        <!-- Expandable Content -->
        @if (isCategoryExpanded(category)) {
        <div class="question-category-content">
          <div class="results-section">
            <div class="performance-table">
              <div class="table-container">
                <table class="audit-table">
                  <thead>
                    <tr>
                      <th>Question</th>
                      <th>Answer</th>
                      <th>Options</th>
                      <th>Evidence</th>
                    </tr>
                  </thead>
                  <tbody>
                    @for (q of questions; track q.question) {
                    <tr [class.passed]="q.pass === 1" [class.failed]="q.pass === 0">
                      <td>{{ q.question }}</td>
                      <td>
                        @if (q.answer) {
                        <span class="answer-value">{{ q.answer }}</span>
                        } @else {
                        <span class="answer-value">-</span>
                        }
                      </td>
                      <td>
                        @if (q.options) {
                        <span class="options-badge">{{ q.options }}</span>
                        } @else {
                        <span class="options-badge">-</span>
                        }
                      </td>
                      <td class="evidence-cell">{{ q.evidence || 'No evidence provided' }}</td>
                    </tr>
                    }
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
        }
      </div>
      }
      }
      }
</div>
} @else if (loading) {
<div class="loading-container">
  <app-common-circle-loader message="Processing AI audit... This may take a few moments."></app-common-circle-loader>
</div>
} @else {
<div class="no-data-container">
  <div class="no-data-content">
    <mat-icon>analytics</mat-icon>
    <h3>No AI Audit Data Available</h3>
    <p>AI audit will be automatically triggered when you click the "Analysis" button in the filter section.</p>
  </div>
</div>
}

@if (auditError) {
<div class="error-container">
  <mat-icon>error</mat-icon>
  <p class="error-message">{{ auditError }}</p>
</div>
}